var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useState, useCallback } from 'react';
import { UXPContext } from '@core/context/Context';
import { CacheManager } from '@core/context/cacheHelper';
import { filterUniqueWidgets, getComponents } from '@utils';
import { BUILTIN_WIDGETS } from '@core/dashboard/builtinWidgets';
import { searchComponents } from '@core/services/services';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_MINUTES = 24 * 60;
// Stored on the unified cache's 'local' backend (non-PII widget catalog — persistence
// is genuinely useful here, and clearFrontendCache() now covers it).
// v2: cached items must carry appRoles/userGroups — bumped so pre-permission caches are ignored
const CACHE_KEY = 'widgetdrawer_v2';
/**
 * Load cache from localStorage
 */
function loadCacheFromStorage() {
    const cached = CacheManager.getInstance().getCachedData(CACHE_KEY, 'local');
    return cached !== null && cached !== void 0 ? cached : { widgets: [], uis: [], categories: [], timestamp: null };
}
/**
 * Save cache to the unified cache (local backend)
 */
function saveCacheToStorage(cache) {
    CacheManager.getInstance().setCachedData(CACHE_KEY, cache, CACHE_MINUTES, 'local');
}
// Initialize module-level cache from localStorage
const cache = loadCacheFromStorage();
/**
 * @export
 * Low-level hook for managing widget/UI cache with localStorage persistence
 * Single source of truth for widget data across the application
 */
export function useWidgetCache(options = {}) {
    var _a;
    const context = useContext(UXPContext);
    const loadWidgetsFromIviva = context.loadWidgetsFromIviva || false;
    const enableUIs = (_a = options.enableUIs) !== null && _a !== void 0 ? _a : true;
    // State
    const [allWidgets, setAllWidgets] = useState(cache.widgets);
    const [allUIs, setAllUIs] = useState(cache.uis);
    const [allCategories, setAllCategories] = useState(cache.categories);
    const [loading, setLoading] = useState(true);
    // Load data on mount (with cache check)
    useEffect(() => {
        // Dev mode: always load fresh from local registry (synchronous, no caching needed)
        if (!loadWidgetsFromIviva) {
            loadData();
            return;
        }
        const isValid = cache.timestamp && Date.now() - cache.timestamp < CACHE_DURATION;
        if (isValid && cache.widgets.length > 0) {
            // Use cached data
            console.log('[useWidgetCache] Using cached data');
            setAllWidgets(cache.widgets);
            setAllUIs(cache.uis);
            setAllCategories(cache.categories);
            setLoading(false);
            return;
        }
        // Cache invalid or empty - load fresh data
        console.log('[useWidgetCache] Cache invalid, loading fresh data');
        loadData();
    }, []);
    // Re-load when a new component is registered after mount (e.g. lazy-loaded app scripts)
    useEffect(() => {
        let timer;
        const handler = () => {
            clearTimeout(timer);
            timer = setTimeout(() => loadData(), 300);
        };
        window.addEventListener('uxp:component-registered', handler);
        return () => {
            window.removeEventListener('uxp:component-registered', handler);
            clearTimeout(timer);
        };
    }, []);
    /**
     * Load all data (widgets and UIs)
     */
    function loadData() {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            const [loadedWidgets, loadedUIs, loadedCategories] = yield loadComponents();
            // Update state
            setAllWidgets(loadedWidgets);
            setAllCategories(loadedCategories);
            setAllUIs(loadedUIs);
            // Only persist to cache when loading from marketplace (not needed in dev mode)
            if (loadWidgetsFromIviva) {
                cache.widgets = loadedWidgets;
                cache.uis = loadedUIs;
                cache.categories = loadedCategories;
                cache.timestamp = Date.now();
                saveCacheToStorage(cache);
                console.log('[useWidgetCache] Cache updated after fresh data load');
            }
            setLoading(false);
        });
    }
    /**
     * Load all components (widgets + UIs) from marketplace or dev environment.
     * Returns [widgets, uis, categories]
     */
    function loadComponents() {
        return __awaiter(this, void 0, void 0, function* () {
            if (loadWidgetsFromIviva) {
                const [widgets, uis, categories] = yield loadFromMarketplace();
                return [widgets, enableUIs ? uis : [], categories];
            }
            else {
                const [widgets, uis, categories] = loadFromDevEnvironment();
                return [widgets, enableUIs ? uis : [], categories];
            }
        });
    }
    /**
     * Load components from dev environment (local registry).
     * Returns [widgets, uis, categories]
     */
    function loadFromDevEnvironment() {
        const widgets = getComponents('widget').map((w) => (Object.assign(Object.assign({}, w), { localizedName: context.$L(w.name), localizedDescription: context.$L(w.description) })));
        const uis = getComponents('ui')
            .filter((u) => { var _a; return !((_a = u === null || u === void 0 ? void 0 : u.id) === null || _a === void 0 ? void 0 : _a.startsWith('uxp-widget-designer-bundle')); })
            .map((u) => (Object.assign(Object.assign({}, u), { localizedName: context.$L(u.name), localizedDescription: context.$L(u.description) })));
        return [widgets, uis, []]; // No categories in dev mode
    }
    /**
     * Load all components from marketplace (search-components returns widgets + UIs + backgrounds).
     * Returns [widgets, uis, categories]
     */
    function loadFromMarketplace() {
        return __awaiter(this, void 0, void 0, function* () {
            const [categories, componentData] = yield Promise.all([
                fetchCategories(),
                fetchWidgets()
            ]);
            const allComponents = (componentData || []).map((w) => (Object.assign(Object.assign({}, w), { localizedName: context.$L(w.name), localizedDescription: context.$L(w.description), category: w.category || 'user-defined' })));
            // Split by mode — search-components returns all types in one response
            const widgets = allComponents
                .filter((w) => !w.modes || w.modes.includes('widget'))
                .sort((a, b) => (a.category || '').localeCompare(b.category || ''));
            const uis = allComponents.filter((w) => { var _a; return (_a = w.modes) === null || _a === void 0 ? void 0 : _a.includes('ui'); }).sort((a, b) => (a.localizedName.localeCompare(b.localizedName)));
            // Marketplace mode shows only backend widgets — but the UXP Core built-ins ship without
            // a bundle and are never returned by the backend, so inject those two directly.
            const builtIns = BUILTIN_WIDGETS.map((w) => (Object.assign(Object.assign({}, w), { localizedName: context.$L(w.name), localizedDescription: context.$L(w.description || ''), category: 'user-defined' })));
            const enhancedCategories = enhanceCategories(categories);
            return [[...builtIns, ...widgets], uis, enhancedCategories];
        });
    }
    /**
     * Fetch widgets from API
     */
    function fetchWidgets() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield searchComponents(context);
            return data ? filterUniqueWidgets(data) : [];
        });
    }
    /**
     * Fetch categories from API
     */
    function fetchCategories() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = 'https://s3-ap-southeast-1.amazonaws.com/marketplace.iviva/widget-categories.json';
            try {
                const res = yield fetch(url);
                if (!res.ok)
                    throw new Error(`HTTP ${res.status}`);
                const data = yield res.json();
                return data.map((c) => (Object.assign(Object.assign({}, c), { code: `uxp-core.widget-category.${c.id}` })));
            }
            catch (e) {
                console.error('Unable to get categories. Error: ', e);
                return [];
            }
        });
    }
    /**
     * Enhance categories with __all__ and user-defined
     */
    function enhanceCategories(categories) {
        if (categories.length === 0)
            return [];
        const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));
        return [
            { id: '__all__', code: 'uxp-core.widget-category.__all__', name: 'All', icon: '' },
            {
                id: 'user-defined',
                code: 'uxp-core.widget-category.user-defined',
                name: 'My Widgets',
                icon: '',
                subCategories: ['user-defined']
            },
            ...sorted
        ];
    }
    /**
     * Invalidate cache and clear data
     */
    const invalidateCache = useCallback(() => {
        cache.widgets = [];
        cache.uis = [];
        cache.categories = [];
        cache.timestamp = null;
        CacheManager.getInstance().removeCachedData(CACHE_KEY, 'local');
        setAllWidgets([]);
        setAllUIs([]);
        setAllCategories([]);
    }, []);
    /**
     * Reload all data
     */
    const reload = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        console.log('[useWidgetCache] Manual reload triggered');
        invalidateCache();
        yield loadData();
    }), [invalidateCache]);
    return {
        allWidgets,
        allUIs,
        allCategories,
        isLoading: loading,
        reload,
        invalidateCache
    };
}
export default useWidgetCache;
